import * as React from 'react';
import { useMemo } from 'react';

import { Card, Group, Stack, Text, Title, useMantineTheme, ActionIcon } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useDeleteStaffAvailabilityException from 'hooks/staffAvailabilityException/useDeleteStaffAvailabilityException';
import useStaffAvailabilityExceptionList from 'hooks/staffAvailabilityException/useStaffAvailabilityExceptionList';
import { StaffAvailabilityException } from 'types';
import notificationHandler from 'utils/notifications';

interface GroupedExceptions {
  [date: string]: StaffAvailabilityException[];
}

const StaffAvailabilityExceptionList = (): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();
  const theme = useMantineTheme();
  const { session } = useAuth();

  const { data } = useStaffAvailabilityExceptionList({
    staffId: session?.staff?.staffId,
  });

  const deleteMutation = useDeleteStaffAvailabilityException();

  const flatData: StaffAvailabilityException[] = useMemo(
    () => data?.pages.flatMap(page => page?.data?.results) ?? [],
    [data],
  );

  const groupedByDate: GroupedExceptions = useMemo(() => {
    return flatData.reduce((groups, exception) => {
      const date = exception.date;

      if (!groups[date]) {
        groups[date] = [];
      }

      groups[date].push(exception);

      return groups;
    }, {} as GroupedExceptions);
  }, [flatData]);

  const sortedDates = useMemo(() => {
    return Object.keys(groupedByDate).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  }, [groupedByDate]);

  const formatTimeSlot = (exception: StaffAvailabilityException): string => {
    const isUnavailable = !exception.startTime && !exception.endTime;

    if (isUnavailable) {
      return 'Unavailable';
    }

    const startTime = dayjs(exception.startTime, 'HH:mm:ss').format('h:mm A');
    const endTime = dayjs(exception.endTime, 'HH:mm:ss').format('h:mm A');

    return `${startTime} - ${endTime}`;
  };

  const handleDeleteException = (staffAvailabilityExceptionId: string): void => {
    deleteMutation.mutate(staffAvailabilityExceptionId, {
      onError: error => {
        errorNotification('Availability exception delete failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Availability exception delete success');
      },
    });
  };

  if (sortedDates.length === 0) {
    return (
      <Card withBorder p='xl'>
        <Text size='md' c='dimmed' ta='center'>
          No availability exceptions found
        </Text>
      </Card>
    );
  }

  return (
    <Stack spacing='lg'>
      {sortedDates.map(date => {
        const dateExceptions = groupedByDate[date];
        const formattedDate = dayjs(date).format('dddd, MMMM D, YYYY');

        const sortedExceptions = [...dateExceptions].sort((a, b) => {
          const aUnavailable = !a.startTime && !a.endTime;
          const bUnavailable = !b.startTime && !b.endTime;

          if (aUnavailable && !bUnavailable) return -1;
          if (!aUnavailable && bUnavailable) return 1;

          if (!aUnavailable && !bUnavailable) {
            return dayjs(a.startTime, 'HH:mm:ss').valueOf() - dayjs(b.startTime, 'HH:mm:ss').valueOf();
          }

          return 0;
        });

        return (
          <Card key={date} withBorder p='lg' bg='gray.0'>
            <Group noWrap grow spacing='md'>
              <Group position='apart' align='flex-start'>
                <Title order={4} c='dark.7'>
                  {formattedDate}
                </Title>
              </Group>

              <Stack spacing={0}>
                {sortedExceptions.map(sae => (
                  <Group key={sae.staffAvailabilityExceptionId} position='right' noWrap>
                    <Group spacing='sm' noWrap>
                      <Text size='md' fw={500} c='dark.7'>
                        {formatTimeSlot(sae)}
                      </Text>
                    </Group>

                    <ActionIcon
                      size='xl'
                      onClick={() => handleDeleteException(sae.staffAvailabilityExceptionId)}
                      title='Delete exception'
                      loading={deleteMutation.isLoading}
                    >
                      <IconTrash size={theme.fontSizes.lg} color={theme.black} />
                    </ActionIcon>
                  </Group>
                ))}
              </Stack>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
};

export default StaffAvailabilityExceptionList;
